import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import {
  Cell,
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Sector,
} from 'recharts';
import { Box } from '@mui/system';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { ThemePalette } from 'mui.theme';
import { FlexBox } from 'components/Containers/FlexBox';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import TableList from './TableList';
import { truncateTo2dp } from 'utils/helpers/general';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { convertsecondsToHours, getUtilizationRowLabel } from '../utils';
import { getDiffForUtilization, returnAnalysis } from 'utils/fuelAnalytics';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  enableDarkTheme: boolean;
  title: string;
  isLoading: boolean;
  customerCode: string;
  data: {
    productiveHours: number;
    productiveHoursDiff: number;
    idling: number;
    idlingDiff: number;
    noOfAssets: number;
    dateRange: number;
    engineHours: number;
    engineHoursDiff: number;
    shiftHours: number;
    shiftHoursDiff: number;
  };
  setHoursBreakDown: any;
}

const HoursBreakdown = ({
  enableDarkTheme,
  title,
  isLoading,
  data,
  setHoursBreakDown,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const onPieClick = (data: any, index: number) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const dataProductiveHours = useMemo(() => data.productiveHours, [data]);

  const dataEngineHours = useMemo(() => data.engineHours, [data]);

  const dataEngineHoursDiff = useMemo(() => data.engineHoursDiff, [data]);

  const dataShiftHours = useMemo(() => data.shiftHours, [data]);

  const dataShiftHoursDiff = useMemo(() => data.shiftHoursDiff, [data]);

  const dataIdling = useMemo(() => data.idling, [data]);

  const maxHours = useMemo(
    () =>
      Number(
        truncateTo2dp(
          dataEngineHours / 60 + (dataShiftHours / 60 - dataEngineHours / 60)
        )
      ),
    [dataEngineHours, dataShiftHours]
  );

  const maxHoursDiff = useMemo(() => {
    const prevEngineHours = dataEngineHours / (1 + dataEngineHoursDiff / 100);

    const prevShiftHours = dataShiftHours / (1 + dataShiftHoursDiff / 100);

    const prevMaxHours = Number(
      truncateTo2dp(
        prevEngineHours / 60 + (prevShiftHours / 60 - prevEngineHours / 60)
      )
    );
    return prevMaxHours !== 0
      ? truncateTo2dp(((maxHours - prevMaxHours) / prevMaxHours) * 100)
      : 0;
  }, [
    dataEngineHours,
    dataEngineHoursDiff,
    dataShiftHours,
    dataShiftHoursDiff,
    maxHours,
  ]);

  const isSame = useMemo(
    () =>
      getDiffForUtilization(dataEngineHoursDiff, dataEngineHours) === 0 &&
      dataEngineHoursDiff !== 0,
    [dataEngineHoursDiff, dataEngineHours]
  );
  const isNotAvailable = useMemo(
    () =>
      !dataEngineHoursDiff ||
      dataEngineHoursDiff === undefined ||
      dataEngineHoursDiff === -100,
    [dataEngineHoursDiff]
  );

  const engHrs = useMemo(
    () =>
      truncateTo2dp(
        dataEngineHours === 0 || dataShiftHours < dataEngineHours
          ? 0
          : dataShiftHours / 60 - dataEngineHours / 60
      ),
    [dataEngineHours, dataShiftHours]
  );

  const tableData = useMemo(
    () => [
      {
        name: 'Engine Hours',
        displayName: t('engine_hours'),
        time: formatNumberToShortScale(truncateTo2dp(dataEngineHours / 60)),
        direction:
          dataEngineHours === 0 ? 'none' : dataEngineHours > 0 ? 'up' : 'down',
        color: _palette.dark.primary,
        fill: _palette.dark.primary,
        maxValue: maxHours,
      },
      {
        name: 'Engine Off Hours',
        displayName: t('engine_off_hours'),
        time: formatNumberToShortScale(engHrs),
        direction:
          Number(engHrs) === 0 ? 'none' : dataShiftHours > 0 ? 'up' : 'down',
        color: '#A776FF',
        fill: '#A776FF',
        maxValue: maxHours,
      },
    ],
    [dataEngineHours, dataShiftHours, maxHours, engHrs, t]
  );

  const unknownState = useMemo(
    () =>
      dataEngineHours / 60 -
      (dataProductiveHours / 60 + convertsecondsToHours(dataIdling)),
    [dataEngineHours, dataProductiveHours, dataIdling]
  );

  const dataList = useMemo(() => {
    return {
      'Engine Hours': [
        {
          name: 'Productive Hours',
          displayName: t('productive_hours'),
          value: Number(truncateTo2dp(dataProductiveHours / 60)),
          direction: dataProductiveHours === 0 ? 'none' : 'up',
          color: '#FF0068',
        },
        {
          name: 'Idling Hours',
          displayName: t('idling_hours'),
          value: Number(truncateTo2dp(convertsecondsToHours(dataIdling))),
          direction: dataIdling === 0 ? 'none' : 'up',
          color: '#FF9C2C',
        },
        {
          name: 'Unknown',
          displayName: t('unknown'),
          value: unknownState > 0 ? truncateTo2dp(unknownState) : 0,
          direction: unknownState === 0 ? 'none' : 'up',
          color: '#C3C3C3',
        },
      ],
      'Engine Off': [
        {
          name: 'Engine Off Hours',
          displayName: t('engine_off_hours'),
          value: engHrs,
          direction: Number(engHrs) === 0 ? 'none' : 'down',
          color: '#A776FF',
        },
      ],
    };
  }, [
    dataEngineHours,
    dataProductiveHours,
    dataIdling,
    engHrs,
    unknownState,
    t,
  ]);

  const pieData = useMemo(
    () => [
      {
        name: 'Engine Hours',
        value: Number(truncateTo2dp(dataEngineHours / 60)),
        direction: dataEngineHours === 0 ? 'none' : 'up',
        color: _palette.dark.primary,
      },
      {
        name: 'Engine Off',
        value: Number(engHrs),
        direction: Number(engHrs) === 0 ? 'none' : 'up',
        color: '#A776FF',
      },
    ],
    [dataEngineHours, engHrs]
  );

  const allData = useMemo(() => {
    return {
      'Productive Hours': Number(truncateTo2dp(dataProductiveHours / 60)),
      'Idling Hours': Number(truncateTo2dp(convertsecondsToHours(dataIdling))),
      'Total Engine Hours': Number(truncateTo2dp(dataEngineHours / 60)),
      'Unknown (hrs)': unknownState > 0 ? truncateTo2dp(unknownState) : 0,
      'Engine Off Hours': truncateTo2dp(
        dataEngineHours === 0 || dataShiftHours < dataEngineHours
          ? 0
          : dataShiftHours / 60 - dataEngineHours / 60
      ),
      'Total Shift Hours': formatNumberToShortScale(truncateTo2dp(maxHours)),
      'Difference from last period': returnAnalysis(maxHoursDiff),
    };
  }, [
    dataProductiveHours,
    dataIdling,
    dataEngineHours,
    dataShiftHours,
    maxHours,
    maxHoursDiff,
    unknownState,
  ]);

  useEffect(() => {
    setHoursBreakDown(allData);
  }, [allData]);

  return (
    <Grid item lg={6} md={6}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.borderGrey}`,
          minHeight: '400px',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            {title}
          </Typography>
          {!isLoading && (
            <Box>
              <Typography
                sx={{
                  fontSize: '13px',
                }}
              >
                {t('total_shift_hours')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '22px',
                }}
              >
                {!isSame && Number(maxHoursDiff) !== 0 ? (
                  Number(maxHoursDiff) > 0 ? (
                    <NorthEastIcon sx={{ color: '#029161', fontSize: 19 }} />
                  ) : (
                    <SouthEastIcon sx={{ color: '#EB5757', fontSize: 19 }} />
                  )
                ) : null}{' '}
                {formatNumberToShortScale(truncateTo2dp(maxHours))}
                <span
                  style={{
                    fontSize: '11px',
                  }}
                >
                  {' '}
                  {t('utilization_unit_hrs')}
                </span>
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                }}
              >
                <span
                  style={{
                    color: isSame
                      ? 'inherit'
                      : Number(maxHoursDiff) > 0
                      ? '#029161'
                      : '#EB5757',
                  }}
                >
                  {isSame && !isNotAvailable
                    ? t('utilization_same')
                    : returnAnalysis(maxHoursDiff)}
                </span>{' '}
                {!returnAnalysis(maxHoursDiff) ? '' : t('from_last_period')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box>
          {isLoading ? (
            <MultiColorCircularLoader height="300px" />
          ) : (
            <>
              <TableList data={tableData} subItems={dataList} />
              <Box>
                <FlexBox
                  sx={{
                    maxWidth: 'inherit',
                    height: '210px',
                    paddingTop: '0px',
                    marginRight: '10px',
                    paddingBottom: '0px',
                  }}
                >
                  {dataEngineHours === 0 ? (
                    <Typography
                      sx={{
                        fontSize: '12px',
                      }}
                    >
                      {t('no_chart_data_available_for_the_selected_time_range')}
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart
                        width={400}
                        height={400}
                        margin={{ top: 10, right: 10, left: 5, bottom: 5 }}
                      >
                        <Pie
                          data={pieData}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={70}
                          onClick={(data, index) => {
                            onPieClick(data, index);
                          }}
                          // @ts-expect-error ignore to display custom shape
                          activeIndex={activeIndex}
                          activeShape={(props: any) =>
                            activeIndex !== null
                              ? renderActiveShape(props, dataList, t)
                              : null
                          }
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                          wrapperStyle={{ outline: 'none', fontSize: '11px' }}
                          contentStyle={{
                            backgroundColor: 'rgba(0,0,0, 0.6)',
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                          }}
                          itemStyle={{
                            color: enableDarkTheme
                              ? ThemePalette.typography.wheat
                              : ThemePalette.typography.black,
                          }}
                          formatter={(value: any, name, props) => {
                            return [
                              `${formatNumberToShortScale(
                                truncateTo2dp(value)
                              )} hrs`,
                              getUtilizationRowLabel(
                                t,
                                typeof name === 'string' ? name : undefined
                              ),
                            ];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </FlexBox>
              </Box>
            </>
          )}
        </Box>
        {/* Add the new Typography component here for the footer text */}
        <Box
          sx={{
            marginTop: '0px',
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: enableDarkTheme
                ? '#F3F4F6'
                : ThemePalette.typography.black,
              paddingTop: '2px',
            }}
          >
            {t('assuming_all_assets_are_running_all_days_during_this_time_pe')}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default HoursBreakdown;

const renderActiveShape = (
  props: any,
  dataList: any,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
  } = props;

  const selectionName: string = payload.payload.name ?? 'Engine Hours';

  const shapeData = dataList[selectionName];

  const totalChildValue = shapeData.reduce(
    (acc: number, child: any) => acc + Number(child.value),
    0
  );

  let currentAngle = startAngle;

  const childSegments = shapeData.map((child: any) => {
    const childAngleDelta: number =
      (child.value / totalChildValue) * (endAngle - startAngle);

    const childStartAngle: number = currentAngle;
    const childEndAngle = Number(currentAngle) + Number(childAngleDelta);
    currentAngle = Number(currentAngle) + Number(childAngleDelta);

    const childAngle = (childStartAngle + childEndAngle) / 2;
    const childSin = Math.sin(-RADIAN * childAngle);
    const childCos = Math.cos(-RADIAN * childAngle);
    const labelX = Number(cx) + (Number(outerRadius) + 24) * childCos;
    const labelY = Number(cy) + (Number(outerRadius) + 22) * childSin;

    return (
      <g key={child.name}>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={Number(outerRadius) + 6}
          outerRadius={Number(outerRadius) + 10}
          startAngle={childStartAngle}
          endAngle={childEndAngle}
          fill={child.color}
        />
        <text
          x={labelX}
          y={labelY}
          textAnchor="middle"
          fill={child.color}
          fontSize={8}
        >
          {`${child.value !== 0 ? child.value : ''}`}
        </text>
      </g>
    );
  });

  return (
    <g>
      <text x={cx} y={cy} textAnchor="middle" fill={fill} fontSize={8}>
        {getUtilizationRowLabel(t, payload.name)}
      </text>

      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      {childSegments}
    </g>
  );
};
